import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../../../utils/apiClient';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Card } from '../../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Badge } from '../../../components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Upload, FileText, Trash2, Edit2, BookOpen, RefreshCw, CheckCircle, XCircle, Loader2, AlertCircle, Eye } from 'lucide-react';
import EmptyState from '../../../components/EmptyState';

export default function KnowledgeTraining() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [textName, setTextName] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [docName, setDocName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [savedKnowledge, setSavedKnowledge] = useState([]);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [itemToRename, setItemToRename] = useState(null);
  const [newName, setNewName] = useState('');
  const [reindexing, setReindexing] = useState({});
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewContent, setViewContent] = useState(null);
  const [loadingContent, setLoadingContent] = useState(false);

  useEffect(() => {
    fetchSavedKnowledge();
  }, []);

  const fetchSavedKnowledge = async () => {
    try {
      const response = await apiClient.get('/vault/items?type=training_text');
      // Filter for Knowledge intent only
      const knowledgeItems = response.data.filter(item => item.intent === 'Knowledge');
      setSavedKnowledge(knowledgeItems);
    } catch (error) {
      console.error('Failed to fetch knowledge training:', error);
      toast.error(t('errors.loadFailed'));
    }
  };

  const handleRetryIndexing = async (item) => {
    setReindexing(prev => ({ ...prev, [item.id]: true }));
    try {
      await apiClient.post(`/rag/reindex/${item.fileId}`);
      toast.success('Document re-indexed successfully');
      fetchSavedKnowledge();
    } catch (error) {
      toast.error('Failed to re-index document');
    } finally {
      setReindexing(prev => ({ ...prev, [item.id]: false }));
    }
  };

  const getRagStatusBadge = (item) => {
    const status = item.ragStatus || (item.indexedForRag ? 'indexed' : 'not_indexed');
    
    switch(status) {
      case 'indexed':
        return (
          <Badge className="text-xs bg-green-100 text-green-700 flex items-center gap-1">
            <CheckCircle className="w-3 h-3" />
            Indexed
          </Badge>
        );
      case 'indexing':
        return (
          <Badge className="text-xs bg-blue-100 text-blue-700 flex items-center gap-1">
            <Loader2 className="w-3 h-3 animate-spin" />
            Indexing
          </Badge>
        );
      case 'updating':
        return (
          <Badge className="text-xs bg-yellow-100 text-yellow-700 flex items-center gap-1">
            <RefreshCw className="w-3 h-3 animate-spin" />
            Updating
          </Badge>
        );
      case 'failed':
        return (
          <Badge className="text-xs bg-red-100 text-red-700 flex items-center gap-1">
            <XCircle className="w-3 h-3" />
            Failed
          </Badge>
        );
      case 'deleting':
        return (
          <Badge className="text-xs bg-gray-100 text-gray-700 flex items-center gap-1">
            <Loader2 className="w-3 h-3 animate-spin" />
            Deleting
          </Badge>
        );
      default:
        return (
          <Badge className="text-xs bg-gray-100 text-gray-500 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            Not Indexed
          </Badge>
        );
    }
  };

  const handleTextSubmit = async () => {
    if (!text.trim()) {
      toast.error(t('errors.uploadFailed'));
      return;
    }

    try {
      const blob = new Blob([text], { type: 'text/plain' });
      const file = new File([blob], `knowledge-${Date.now()}.txt`, { type: 'text/plain' });

      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', textName || `Knowledge - ${new Date().toLocaleDateString()}`);
      formData.append('description', `Category: Knowledge | ${text.substring(0, 100)}...`);
      formData.append('type', 'training_text');
      formData.append('tags', 'training,knowledge,text');
      formData.append('allowForAI', 'true');
      formData.append('intent', 'Knowledge');

      await apiClient.post('/vault/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success(t('success.saved'));
      setText('');
      setTextName('');
      fetchSavedKnowledge();
    } catch (error) {
      toast.error(t('errors.saveFailed'));
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('title', docName || selectedFile.name);
      formData.append('description', 'Category: Knowledge | Reference document');
      formData.append('type', 'training_text');
      formData.append('tags', 'training,knowledge,document');
      formData.append('allowForAI', 'true');
      formData.append('intent', 'Knowledge');

      await apiClient.post('/vault/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success(t('success.uploaded'));
      setSelectedFile(null);
      setDocName('');
      fetchSavedKnowledge();
    } catch (error) {
      toast.error(t('errors.uploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  const openRenameDialog = (item) => {
    setItemToRename(item);
    setNewName(item.title);
    setRenameDialogOpen(true);
  };

  const openViewDialog = async (item) => {
    setLoadingContent(true);
    setViewDialogOpen(true);
    try {
      const response = await apiClient.get(`/vault/content/${item.fileId}`);
      setViewContent(response.data);
    } catch (error) {
      console.error('Failed to fetch content:', error);
      toast.error('Failed to load content');
      setViewContent({ content: '', title: item.title, previewStatus: 'failed', previewType: 'unknown' });
    } finally {
      setLoadingContent(false);
    }
  };

  // Helper function to get preview type label
  const getPreviewLabel = (previewType, previewStatus) => {
    if (previewStatus === 'failed' || previewStatus === 'unsupported') {
      return { label: 'Preview Not Available', color: 'bg-gray-100 text-gray-600' };
    }
    
    switch (previewType) {
      case 'docx':
        return { label: 'DOCX · Extracted Text Preview', color: 'bg-blue-50 text-blue-700' };
      case 'pdf':
        if (previewStatus === 'partial') {
          return { label: 'PDF · Partial Text Preview', color: 'bg-amber-50 text-amber-700' };
        }
        return { label: 'PDF · Text Preview', color: 'bg-blue-50 text-blue-700' };
      case 'txt':
        return { label: 'TXT · Original Content', color: 'bg-green-50 text-green-700' };
      case 'pasted':
        return { label: 'Text Entry · Original Content', color: 'bg-green-50 text-green-700' };
      case 'rtf':
        if (previewStatus === 'partial') {
          return { label: 'RTF · Partial Text Preview', color: 'bg-amber-50 text-amber-700' };
        }
        return { label: 'RTF · Text Preview', color: 'bg-blue-50 text-blue-700' };
      case 'doc':
        return { label: 'DOC · Preview Not Available', color: 'bg-gray-100 text-gray-600' };
      default:
        return { label: 'Unknown · Text Preview', color: 'bg-gray-50 text-gray-600' };
    }
  };

  const handleRename = async () => {
    try {
      await apiClient.put(`/vault/items/${itemToRename.id}?title=${encodeURIComponent(newName)}`);
      toast.success(t('success.updated'));
      setRenameDialogOpen(false);
      fetchSavedKnowledge();
    } catch (error) {
      toast.error(t('errors.saveFailed'));
    }
  };

  const handleDelete = async (itemId) => {
    if (!confirm(t('mySkills.confirmRemove'))) return;

    try {
      await apiClient.delete(`/vault/items/${itemId}`);
      toast.success(t('success.deleted'));
      fetchSavedKnowledge();
    } catch (error) {
      toast.error(t('errors.deleteFailed'));
    }
  };

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/personal/twin/train')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('common.back')}
        </Button>
        <div>
          <h1 className="font-heading text-3xl font-bold text-gray-900">{t('training.knowledgeTraining')}</h1>
          <p className="text-gray-600 mt-1">{t('training.whatYouKnow')}</p>
        </div>
      </div>

      <Card className="p-4 rounded-2xl bg-blue-50 border-blue-200">
        <div className="flex items-start gap-3">
          <BookOpen className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-900">
            <p className="font-medium mb-1">Knowledge vs Style</p>
            <p className="text-blue-800">
              Knowledge training provides information your Twin can <strong>cite, summarize, or reference</strong>.
              Use this for facts, FAQs, product docs, or any content you want your Twin to know about.
            </p>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="p-6 rounded-2xl space-y-4">
          <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
            <FileText className="w-6 h-6 text-blue-600" />
          </div>
          <h3 className="font-heading text-xl font-semibold">Paste Information</h3>
          <p className="text-sm text-gray-600">
            Paste facts, notes, or any information you want your Twin to remember
          </p>

          <div>
            <Label>Title (Optional)</Label>
            <Input
              value={textName}
              onChange={(e) => setTextName(e.target.value)}
              placeholder="e.g., Product FAQ, Company Info"
              className="mt-1"
            />
          </div>

          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t('placeholders.knowledgeContent')}
            rows={8}
            className="resize-none"
          />
          
          <Button onClick={handleTextSubmit} className="w-full bg-blue-600 hover:bg-blue-700" disabled={!text.trim()}>
            Save Knowledge
          </Button>
        </Card>

        <Card className="p-6 rounded-2xl space-y-4">
          <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
            <Upload className="w-6 h-6 text-blue-600" />
          </div>
          <h3 className="font-heading text-xl font-semibold">Upload Reference Documents</h3>
          <p className="text-sm text-gray-600">
            Upload PDF, TXT, DOC, or DOCX files with reference information
          </p>

          <label htmlFor="doc-upload">
            <div className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all ${
              selectedFile 
                ? 'border-blue-600 bg-blue-50' 
                : 'border-gray-300 hover:border-blue-300'
            }`}>
              {selectedFile ? (
                <div>
                  <FileText className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                  <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
                  <p className="text-xs text-gray-500">{(selectedFile.size / 1024).toFixed(1)} KB</p>
                </div>
              ) : (
                <>
                  <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-900">Click to select document</p>
                </>
              )}
            </div>
            <input
              id="doc-upload"
              type="file"
              accept=".pdf,.txt,.doc,.docx"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  setSelectedFile(file);
                  setDocName(file.name);
                }
              }}
            />
          </label>

          {selectedFile && (
            <>
              <div>
                <Label>{t('forms.nameOptional')}</Label>
                <Input
                  value={docName}
                  onChange={(e) => setDocName(e.target.value)}
                  placeholder={t('training.documentNamePlaceholder')}
                  className="mt-1"
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={() => { setSelectedFile(null); setDocName(''); }} variant="outline" className="flex-1">
                  Clear
                </Button>
                <Button onClick={handleFileUpload} disabled={uploading} className="flex-1 bg-blue-600 hover:bg-blue-700">
                  {uploading ? 'Uploading...' : 'Upload'}
                </Button>
              </div>
            </>
          )}
        </Card>
      </div>

      <Card className="p-6 rounded-2xl">
        <h3 className="font-semibold text-gray-900 mb-4">Saved Knowledge ({savedKnowledge.length})</h3>
        {savedKnowledge.length > 0 ? (
          <div className="space-y-2">
            {savedKnowledge.map((item) => (
              <div key={item.id} className="flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:border-blue-300 transition-all">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <p className="text-sm font-medium text-gray-900">{item.title}</p>
                    <Badge className="text-xs bg-blue-100 text-blue-700">
                      Knowledge
                    </Badge>
                    {getRagStatusBadge(item)}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span>{new Date(item.createdAt).toLocaleDateString()}</span>
                    <span>•</span>
                    <span>{((item.fileSize || 0) / 1024).toFixed(1)} KB</span>
                    {item.ragVersion > 0 && (
                      <>
                        <span>•</span>
                        <span>v{item.ragVersion}</span>
                      </>
                    )}
                  </div>
                  {item.ragStatus === 'failed' && item.ragLastError && (
                    <p className="text-xs text-red-600 mt-1 truncate" title={item.ragLastError}>
                      Error: {item.ragLastError}
                    </p>
                  )}
                </div>
                <div className="flex gap-1 items-center">
                  {item.ragStatus === 'failed' && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleRetryIndexing(item)}
                      disabled={reindexing[item.id]}
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                    >
                      {reindexing[item.id] ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4" />
                      )}
                      <span className="ml-1 text-xs">Retry</span>
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => openViewDialog(item)} title="View content">
                    <Eye className="w-4 h-4 text-gray-400 hover:text-blue-600" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => openRenameDialog(item)} title="Rename">
                    <Edit2 className="w-4 h-4 text-gray-400 hover:text-blue-600" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)} title="Delete">
                    <Trash2 className="w-4 h-4 text-gray-400 hover:text-red-600" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            icon={BookOpen}
            title={t('emptyStates.noKnowledgeData')}
            description={t('emptyStates.noKnowledgeDesc')}
            actionLabel={t('emptyStates.uploadDocument')}
            onAction={() => document.querySelector('input[type="file"]')?.click()}
            secondaryText="💡 Tip: Include company info, product details, or any factual information your Twin needs"
          />
        )}
      </Card>

      <Card className="p-6 rounded-2xl bg-blue-50 border-blue-200">
        <h3 className="font-semibold text-blue-900 mb-2">Tips for Better Knowledge Training</h3>
        <ul className="space-y-2 text-sm text-blue-800">
          <li className="flex items-start gap-2">
            <span className="text-blue-600 mt-1">•</span>
            <span>Upload structured documents (PDFs with headings, bullet points)</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-600 mt-1">•</span>
            <span>Provide comprehensive information across different topics</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-600 mt-1">•</span>
            <span>Include details and context, not just brief notes</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-600 mt-1">•</span>
            <span>Keep documents updated to maintain knowledge freshness</span>
          </li>
        </ul>
      </Card>

      {/* Rename Dialog */}
      <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename Knowledge Item</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>New Name</Label>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Enter new name"
                className="mt-1"
              />
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setRenameDialogOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleRename} className="flex-1 bg-blue-600 hover:bg-blue-700" disabled={!newName.trim()}>
                Rename
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Content Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-blue-600" />
              {viewContent?.title || 'Knowledge Content'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {loadingContent ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                <span className="ml-2 text-gray-600">Loading content...</span>
              </div>
            ) : viewContent ? (
              <>
                {/* File info and preview type label */}
                <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                  <Badge className="bg-blue-100 text-blue-700">Knowledge</Badge>
                  <span>•</span>
                  <span>{viewContent.fileName}</span>
                  <span>•</span>
                  <span>{((viewContent.fileSize || 0) / 1024).toFixed(1)} KB</span>
                </div>
                
                {/* Preview type label */}
                {viewContent.previewType && (
                  <div className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${getPreviewLabel(viewContent.previewType, viewContent.previewStatus).color}`}>
                    {getPreviewLabel(viewContent.previewType, viewContent.previewStatus).label}
                  </div>
                )}
                
                {/* Content display or fallback message */}
                {viewContent.previewStatus === 'failed' || viewContent.previewStatus === 'unsupported' || !viewContent.content ? (
                  <div className="bg-gray-50 rounded-lg p-6 text-center">
                    <AlertCircle className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600 font-medium">Preview not available for this file format.</p>
                    <p className="text-sm text-gray-500 mt-1">
                      The content has been indexed for your Twin to use, but cannot be displayed here.
                    </p>
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-lg p-4 max-h-[50vh] overflow-y-auto">
                    <pre className="whitespace-pre-wrap text-sm text-gray-800 font-mono">
                      {viewContent.content}
                    </pre>
                  </div>
                )}
                
                <p className="text-xs text-gray-500 italic">
                  This content is used by your Twin to answer questions and provide accurate information.
                </p>
              </>
            ) : null}
            <div className="flex justify-end">
              <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
